import { v5 as uuidv5 } from 'uuid';
import { BACKEND_STATE } from '../core/constants';

import { getDomainCfg } from "../core/state";
import { getComponentData, setComponentData } from '../core/views';
import { initTerminalFrame } from "./coderunner";
import { doRequest, requestTextContent } from './http';
import { createZipDataBase64, createZipFile } from './zipping';
import { finished } from 'stream';

function getBackendState() {
  return getComponentData(BACKEND_STATE);
}

function setBackendState(state : any) {
  setComponentData(BACKEND_STATE, state);
}

export const backendStates = {
  initializing : "INITIALIZING",
  initialized : "INITIALIZED",
  connecting : "CONNECTING",
  connected : "CONNECTED",
  uploading : "UPLOADING",
  uploaded : "UPLOADED",
  error: "ERROR",
  requesting : "REQUESTING",
  executing : "EXECUTING",
  finished : "FINISHED",
  online : "ONLINE",
  offline : "OFFLINE"
}

export function getBackendStatusText(sessionState) {  
  switch(sessionState) {
    case backendStates.initializing:
      return "backend-initializing";
    case backendStates.initialized:
      return "backend-initialized";
    case backendStates.connecting:
      return "backend-connecting";
    case backendStates.connected:
      return "backend-connected";
    case backendStates.uploading:
      return "backend-uploading";
    case backendStates.uploaded:
      return "backend-uploaded";
    case backendStates.error:
      return "backend-error";
    case backendStates.requesting:
      return "backend-requesting";
    case backendStates.executing:
      return "backend-executing";
    case backendStates.finished:
      return "backend-finished";
    case backendStates.online:
      return "backend-online";
    case backendStates.offline:
      return "backend-offline";
    default:
      return "backend-unknown";
  }
}

export function setBackendStatus(sessionState) {
  const backendState = getBackendState();
  const newBackendState = {
    ... backendState,
    sessionState : sessionState,
  }
  setBackendState(newBackendState);
}

function getBackendHost() {
  let host = getDomainCfg().serverSettings.backendUrl;
  if (getDomainCfg().serverSettings.connPort !== undefined) {
    host += ":" + getDomainCfg().serverSettings.connPort;
  }
  return host;
}

function getBackendIoServer() {
  let backendServer = getDomainCfg().serverSettings.backendUrl;
  if (getDomainCfg().serverSettings.ioPort !== undefined) {
    backendServer += ":" + getDomainCfg().serverSettings.ioPort;
  }
  return backendServer;
}

export function backendActive(state? : any) {
  if (!state.cfg?.editorFeatures?.backendActive || !state) {
    return false;
  }
  const supportedBackendLangs = getDomainCfg().serverSettings.supportedBackendLangs;  
  return supportedBackendLangs.includes(state.project.language);
}

export const initBackend = async (state : any, initCode : string) => {
  console.log("CO-BACKEND: initBackend called", {
    hasUserSession: !!getBackendState()?.userSession,
    sessionState: getBackendState()?.sessionState,
    language: state?.project?.language,
    isInitializing: getBackendState()?.initializing
  });
  
  // Atomic check: prevent multiple simultaneous initializations
  const currentState = getBackendState();
  if (currentState?.userSession) {
    console.log('CO-BACKEND: initBackend - user session already exists, skipping');
    return;
  }
  
  if (currentState?.initializing) {
    console.log('CO-BACKEND: initBackend - initialization already in progress, skipping');
    return;
  }
  
  // Set initializing flag atomically
  setBackendState({
    ...currentState,
    initializing: true
  });
  
  try {
    const data = await checkBackendServerAvailable();
    const backendSession = getBackendState();
    
    // Double-check after async operation (another call might have completed)
    if (!backendSession.userSession) {
      console.log('CO-BACKEND: request new user session - NO existing session');
      await requestUserSession(state, initCode, "plain", data.key);
    } else {
      console.log('CO-BACKEND: initBackend - user session was created by another call, skipping');
    }
  } finally {
    // Clear initializing flag
    const finalState = getBackendState();
    setBackendState({
      ...finalState,
      initializing: false
    });
  }
}

export const runBackendCode = async (state? : any) : Promise<string> => {
  console.log('CO-BACKEND: runBackendCode called (START button pressed)', {
    hasTabs: !!(state.project.tabs && state.project.tabs.length > 0),
    language: state?.project?.language,
    hasUserSession: !!getBackendState()?.userSession
  });
  
  if (state.project.tabs && state.project.tabs.length > 0) {
    const compressedCode = await compressCode(state);
    console.log("CO-BACKEND: runBackendCode -> using compressed code");
    return await requestBackendExecutor(state, compressedCode, "compressed");
  } else {
    const backendCode = mergeCode(state);
    console.log("CO-BACKEND: runBackendCode -> using plain code", { codeLength: backendCode.length });
    return await requestBackendExecutor(state, backendCode, "plain");
  }
}

async function requestBackendExecutor(state : any, backendCode : string, codeType : string) {
  console.log("CO-BACKEND: requestBackendExecutor called", {
    codeType,
    codeLength: backendCode?.length || 0,
    language: state?.project?.language
  });
  
  const data = await checkBackendServerAvailable();
  console.log("CO-BACKEND: server available");
  await getUserSession(state, backendCode, codeType, data.key);
  console.log("CO-BACKEND: requestBackendExecutor -> calling connectSocket");
  return connectSocket(state);
}

function connectSocket(state) {
  let backendState = getBackendState();
  console.log("connect to socket user session");

  const terminalData = {
    ... backendState.userSession, // TODO: check if everything is needed in userSession
    code : undefined,    
    host : getBackendHost(),
    server : getBackendIoServer(),
    path : undefined, //TODO: needed?
    file : getStartFile(state),
    sessionState : backendState.sessionState,
  }
  
  return initTerminalFrame(state, terminalData);
}

function getStartFile(state:any) {
  if (state.project.tabs && state.project.tabs.length > 0) {
    return state.project.tabs[0].name;
  }

  return "code." + state.project.language;
}

async function getUserSession(state:any, code, type, serverKey) {
  let backendSession = getBackendState();  
  console.log('get user session')
  let retriesLeft = 5;
  do {
    backendSession = getBackendState();  
    if (!backendSession.userSession) {
      console.log('request new user session')
      await requestUserSession(state, code, type, serverKey);
    } else {
      console.log('use user session')
      await uploadFileToUserSession(code, type);
      await connectToUserSession();
    }
    backendSession = getBackendState();  
    console.log('user session:', backendSession.userSession, backendSession.retrying)
    retriesLeft--;
  } while ((!backendSession.userSession || backendSession.retrying) && retriesLeft > 0);
  return backendSession.userSession;  
}

async function requestUserSession(state:any, code, type, serverKey) {
  console.log("CO-BACKEND: requestUserSession called", {
    language: state.project.language,
    codeType: type,
    codeLength: code?.length || 0,
    hasServerKey: !!serverKey
  });

  //TODO: get language from config
  const language = state.project.language === "node" ? "javascript" : state.project.language;
  const userData = generateClientData();

  let backendState = getBackendState()
  backendState = {
    ...backendState,
    cfg : {
      serverKey,
      code,
      type,
      language,
      userKey : userData.userKey,
      userId : userData.userId    
    },
    sessionState : backendStates.initializing,
  }
  setBackendState(backendState);

  const codeExecRequestData = {
    code: encodeURI(code),
    lang: language,
    type,
    serverKey:  backendState.cfg.serverKey,
    userKey : backendState.cfg.userKey
  }

  console.log("CO-BACKEND: requestUserSession -> calling startBackendSession", codeExecRequestData);
  await startBackendSession(codeExecRequestData);
  console.log("CO-BACKEND: requestUserSession -> startBackendSession completed");
}

function generateClientData() {
  const randomId = Math.floor(Math.random() * 1200) + 1000;
  const userId = "user-" + Date.now() + "-" + randomId;

  const namespace = uuidv5(getDomainCfg().baseUrl, uuidv5.URL);
  const userKey = uuidv5(userId, namespace);
  console.log("Generated Client data:", userKey, userId);

  return { userId, userKey };
}

async function startBackendSession(requestBody) {
  const url = getBackendHost() + "/start"
  const headers = new Headers();
  headers.append("Accept", "application/json");
  headers.append("Content-Type", "application/json; charset=utf-8");
  const settings = {
    credentials: "include",
    headers,
    method: "POST",
    body: JSON.stringify(requestBody)
  }

  try {
    const response = await doRequest(url, settings);
    console.log("response", response);
    let backendState = getBackendState()
    backendState = {
      ...backendState,
      userSession : response,
      sessionState : backendStates.initialized
    }
    setBackendState(backendState);
  } catch(err) {
    console.log("fail", err);
  }
}

async function connectToUserSession() {
  let backendState = getBackendState()
  backendState = {
    ...backendState,
    cfg : {
      ...backendState.cfg      
    },
    sessionState : backendStates.connecting,
  }
  setBackendState(backendState);

  const codeExecRequestData = {
    serverKey:  backendState.cfg.serverKey,
    userKey : backendState.cfg.userKey,
    ...backendState.userSession
  }

  console.log("request code exec with", codeExecRequestData);
  await connectBackendSession(codeExecRequestData);
}

async function connectBackendSession(requestBody) {
  const url = getBackendHost() + "/connect"
  const headers = new Headers();
  headers.append("Accept", "application/json");
  headers.append("Content-Type", "application/json; charset=utf-8");
  const settings = {
    credentials: "include",
    headers,
    method: "POST",
    body: JSON.stringify(requestBody)
  }

  try {
    const response = await doRequest(url, settings);
    if (!response.invalid) {
      console.log("response", response);
      let backendState = getBackendState()
      backendState = {
        ...backendState,
        userSession : response,
        sessionState : backendStates.connected,
        retrying : false
      }
      setBackendState(backendState);
    } else {
      setErrorState();
    }
  } catch(err) {
    console.log("fail", err);
  }
}

async function uploadFileToUserSession(code, type) {
  // code like in connectToUserSession but with /upload
  let backendState = getBackendState()
  backendState = {
    ...backendState,
    cfg : {
      ...backendState.cfg,
      code
    },
    sessionState : backendStates.uploading,
  }
  setBackendState(backendState);

  const codeExecRequestData = {
    fileContent: encodeURI(code),
    type,
    serverKey:  backendState.cfg.serverKey,
    userKey : backendState.cfg.userKey,
    ...backendState.userSession
  }

  console.log("request code exec with", codeExecRequestData);
  await uploadBackendSession(codeExecRequestData);
}

async function uploadBackendSession(requestBody) {
  const url = getBackendHost() + "/upload"
  const headers = new Headers();
  headers.append("Accept", "application/json");
  headers.append("Content-Type", "application/json; charset=utf-8");
  const settings = {
    credentials: "include",
    headers,
    method: "POST",
    body: JSON.stringify(requestBody)
  }

  try {
    const response = await doRequest(url, settings);

    console.log("response", response);
    let backendState = getBackendState()
    if (!response.invalid) {
      backendState = {
        ...backendState,      
        sessionState : backendStates.uploaded,
        retrying : false
      }
      setBackendState(backendState);
    } else {
      setErrorState();
    }
  } catch(err) {
    console.log("failed", err);
  }
}

async function checkBackendServerAvailable() {
  const url = getBackendHost() + "/status";
  const settings = {
    "crossDomain": true,
    "method": "GET"
  }

  try {
    const response = await requestTextContent(url, settings);
    return {key:response};
  } catch(err) {
    console.log("status response failed", err);
  }
  return {key:null};
}

async function compressCode(state) {
  
  const compressedCode = {
    files : []
  };
  for (let i = 0; i < state.project.tabs.length; i++) {
    console.log("tab", state.project.tabs[i]);
    
    let source = "";
    if (state.data.editorTabs === undefined || state.data.editorTabs[i] === undefined) {
      source = state.data.projectContent.tabs[i].code;
    } else {
      source = state.data.editorTabs[i].code;
    }

    let fileName = state.project.tabs[i].name; 

    const file = {
      fileName : fileName,
      content : toUTF8Array(source)
    }
    compressedCode.files.push(file);
  }

  const zipFile = await createZipDataBase64("data.zip", compressedCode.files);
  console.log('zipFile', zipFile);
  return zipFile;
}

function toUTF8Array(str) {
  var utf8 = [];
  for (var i=0; i < str.length; i++) {
    var charcode = str.charCodeAt(i);
    if (charcode < 0x80) utf8.push(charcode);
    else if (charcode < 0x800) {
      utf8.push(0xc0 | (charcode >> 6), 
                0x80 | (charcode & 0x3f));
    } else if (charcode < 0xd800 || charcode >= 0xe000) {
      utf8.push(0xe0 | (charcode >> 12), 
                0x80 | ((charcode>>6) & 0x3f), 
                0x80 | (charcode & 0x3f));
    }
    // surrogate pair
    else {
      i++;
      // UTF-16 encodes 0x10000-0x10FFFF by
      // subtracting 0x10000 and splitting the
      // 20 bits of 0x0-0xFFFFF into two halves
      charcode = 0x10000 + (((charcode & 0x3ff)<<10)
                         | (str.charCodeAt(i) & 0x3ff));
      utf8.push(0xf0 | (charcode >>18), 
                0x80 | ((charcode>>12) & 0x3f), 
                0x80 | ((charcode>>6) & 0x3f), 
                0x80 | (charcode & 0x3f));
    }
  }
  return utf8;
}

function mergeCode(state) {
  let backendCode = "";

  if (state.data.projectContent.initCode && state.data.projectContent.initCode.length > 0) {
    backendCode += state.data.projectContent.initCode;
  }

  if (state.data.editorCode) {
    backendCode += state.data.editorCode;
  } else {
    backendCode += state.data.projectContent.projectSource;
  }
  return backendCode;
}

function setErrorState() {
  let backendState = getBackendState()
  backendState = {
    ...backendState,
    userSession : undefined,
    sessionState : backendStates.error,
    retrying : true,
  }
  setBackendState(backendState);
}